import { isUserPreferenceEnabled, type UserPreferenceEntry, type UserPreferenceKey } from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useReducer } from 'react';

import { fetchUserPreferences, resetUserPreferencesApi, setUserPreferenceApi } from 'src/auth/userPreferenceApi';

import { getAuthToken, useUser } from './UserContext';

interface State {
  userPreferences: UserPreferenceEntry[];
  loading: boolean;
  shouldFetch: boolean;
}

type Action =
  | { type: 'IS_FETCHING' }
  | { type: 'FETCHED'; payload: UserPreferenceEntry[] }
  | { type: 'UPSERT'; payload: UserPreferenceEntry }
  | { type: 'REFETCH' }
  | { type: 'RESET'; payload: UserPreferenceKey[] };

const initialState: State = {
  userPreferences: [],
  loading: true,
  shouldFetch: true,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'IS_FETCHING':
      return { ...state, loading: true };
    case 'FETCHED':
      return { ...state, loading: false, shouldFetch: false, userPreferences: action.payload };
    case 'UPSERT': {
      const others = state.userPreferences.filter((preference) => preference.name !== action.payload.name);
      return { ...state, userPreferences: [...others, action.payload] };
    }
    case 'REFETCH':
      return { ...state, shouldFetch: true };
    case 'RESET':
      return {
        ...state,
        userPreferences: state.userPreferences.filter(
          (preference) => !action.payload.some((key) => key === preference.name)
        ),
      };
    default:
      return state;
  }
};

export interface UserPreferencesContextState extends State {
  isPreferenceEnabled: (key: UserPreferenceKey) => boolean;
  setPreferenceValue: (key: UserPreferenceKey, value: string) => Promise<void>;
  resetUserPreferences: (keys: UserPreferenceKey[]) => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextState | null>(null);

const STALE_PREFERENCE_SCOPE = 'The user preference operation outlived the session it was queued in';

let pendingPreferenceOperations: Promise<unknown> = Promise.resolve();

export const readSessionScope = async (): Promise<string | null> => {
  const token = await getAuthToken();
  if (isNil(token)) {
    return null;
  }
  try {
    const payloadSegment = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { sub, clientId } = JSON.parse(atob(payloadSegment));
    return isNil(sub) || isNil(clientId) ? null : `${clientId}#${sub}`;
  } catch (error) {
    console.error('[UserPreferencesContext] reading the session scope from the token failed', error);
    return null;
  }
};

const enqueuePreferenceOperation = <TResult,>(runOperation: () => Promise<TResult>): Promise<TResult> => {
  const scopeWhenQueued = readSessionScope();
  const runWhenScopeIsStillActive = async (): Promise<TResult> => {
    const [queuedScope, currentScope] = await Promise.all([scopeWhenQueued, readSessionScope()]);
    if (isNil(queuedScope) || queuedScope !== currentScope) {
      throw new Error(STALE_PREFERENCE_SCOPE);
    }
    return runOperation();
  };
  const nextOperation = pendingPreferenceOperations.then(runWhenScopeIsStillActive, runWhenScopeIsStillActive);
  pendingPreferenceOperations = nextOperation.catch(() => undefined);
  return nextOperation;
};

export const UserPreferencesContextProvider = ({ children }: { children?: ReactNode }) => {
  const { clientId } = useUser();
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      dispatch({ type: 'IS_FETCHING' });
      try {
        const userPreferences = await enqueuePreferenceOperation(fetchUserPreferences);
        if (!unmounted) {
          dispatch({ type: 'FETCHED', payload: userPreferences });
        }
      } catch (error) {
        console.error('[UserPreferencesContext] fetching the user preferences failed', error);
        if (!unmounted) {
          dispatch({ type: 'FETCHED', payload: [] });
        }
      }
    };
    if (state.shouldFetch && !isNil(clientId)) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch, clientId]);

  const setPreferenceValue = async (key: UserPreferenceKey, value: string) => {
    dispatch({ type: 'UPSERT', payload: { name: key, value } });
    try {
      const preference = await enqueuePreferenceOperation(() => setUserPreferenceApi(key, value));
      dispatch({ type: 'UPSERT', payload: preference });
    } catch (error) {
      dispatch({ type: 'REFETCH' });
      throw error;
    }
  };

  const resetUserPreferences = async (keys: UserPreferenceKey[]) => {
    dispatch({ type: 'RESET', payload: keys });
    try {
      await enqueuePreferenceOperation(() => resetUserPreferencesApi(keys));
      dispatch({ type: 'RESET', payload: keys });
    } catch (error) {
      dispatch({ type: 'REFETCH' });
      throw error;
    }
  };

  const values = useMemo(
    () => ({
      ...state,
      isPreferenceEnabled: (key: UserPreferenceKey) => isUserPreferenceEnabled(key, state.userPreferences),
      setPreferenceValue,
      resetUserPreferences,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <UserPreferencesContext.Provider value={values}>{children}</UserPreferencesContext.Provider>;
};

export const useUserPreferences = (): UserPreferencesContextState => {
  const context = useContext(UserPreferencesContext);
  if (isNil(context)) {
    throw new Error('`useUserPreferences` hook must be used within a `UserPreferencesContextProvider` component');
  }
  return context;
};
